import React from 'react';
import { observer } from 'mobx-react';

import { UiButton,
    UiDatepicker,
    UiDescription,
    UiForm,
    UiFormControl,
    UiIcon,
    UiInput,
    UiLoading,
    UiModalActions,
    UiModalContent,
    UiModalTitle,
    UiSelect,
    UiUserSelect, } from 'shared/uikit-new';
import { useAsyncEffect, useObservable, useUser, useValidationZod } from 'shared/hooks';
import { ITaskModel, SpaceModel, TaskModel } from 'shared/models/TaskTracker';
import { TaskTrackerTaskPriorityEnum } from 'shared/enums';
import { spacesGetQuery, spacesQuery, tasksGetQuery, tasksSaveQuery } from 'shared/queries/TaskTracker';
import { COLORS } from 'shared/constants';
import { OnChangeHandlerType } from 'shared/types';
import { CTaskFiles } from 'shared/components/TaskTracker/CTaskFiles';
import { IUserModel } from 'shared/models';

import './styles.scss';

type PropsType = {
    taskTrackerTask?: ITaskModel;
    onClose?: () => void;
    onSaved?: (task: TaskModel) => void;
    onChangeState?: (state: string) => void;
};

export const MdTaskTrackerTaskSave = observer(function MdTaskTrackerTaskSave({
    taskTrackerTask: taskTrackerTaskData = {},
    onClose = () => undefined,
    onSaved = () => undefined,
    onChangeState = () => undefined,
}: PropsType) {
    const user = useUser();
    const store = useObservable({
        taskTrackerSpaces: [] as SpaceModel[],
        taskTrackerTask: new TaskModel(taskTrackerTaskData),
        isLoading: true,
        isSubmitting: false,
        isSubmitted: false,
        priorityIsChanged: false,
        isAssignmentOnly: false,
        newFiles: [] as File[],
    });

    useAsyncEffect(async () => {
        store.reset();
        store.set('taskTrackerTask', new TaskModel(taskTrackerTaskData));

        const responseTaskTrackerSpaces = await spacesQuery();
        if (responseTaskTrackerSpaces.isSuccess && responseTaskTrackerSpaces.data) {
            store.set('taskTrackerSpaces', responseTaskTrackerSpaces.data.items.map(item => new SpaceModel(item)));
        }

        if (!store.taskTrackerTask.id) {
            store.taskTrackerTask.update({
                ownerUserId: user.id,
            });
            store.set('isLoading', false);
            return;
        }

        const response = await tasksGetQuery({ id: store.taskTrackerTask.id });
        if (response.isSuccess && response.data) {
            store.set('taskTrackerTask', new TaskModel(response.data.item));
        }
        store.set('isAssignmentOnly', store.taskTrackerTask.isAssignmentOnly(user));
        store.set('isLoading', false);
    }, [taskTrackerTaskData, store]);

    const validation = useValidationZod(store.taskTrackerTask, z => ({
        name: z.string().min(1, 'обязательное поле'),
    }));

    const getResponsibleUserId = (owners: IUserModel[]) => {
        if (owners.length !== 1) {
            return null;
        }

        return owners[0].id;
    };

    const handleChange = async <R,>(data: { name: string; value: R }) => {
        if (data.name === 'priorityId' && store.taskTrackerTask.id > 0) {
            store.set('priorityIsChanged', true);
        }

        if (data.name !== 'spaceId') {
            store.taskTrackerTask.handleChange(data);
            return;
        }

        const response = await spacesGetQuery({ id: +(data.value as number) });
        const responsibleUserId = response.isSuccess && response.data
            ? getResponsibleUserId(response.data.owners)
            : null;

        store.taskTrackerTask.update({
            spaceId: data.value,
            responsibleUserId,
        });
    };
    const isAssignmentOnly = store.isAssignmentOnly;

    const handleSubmit = async () => {
        store.set('isSubmitted', true);

        if (store.isSubmitting || !validation.isValid || (!store.taskTrackerTask.boardId && !store.taskTrackerTask.spaceId)) {
            return;
        }

        store.set('isSubmitting', true);

        const task = store.taskTrackerTask.toObject();
        const response = await tasksSaveQuery(isAssignmentOnly
            ? {
                id: task.id,
                responsibleUserId: task.responsibleUserId,
                users: task.users,
            }
            : {
                ...task,
                newFiles: store.newFiles,
            });
        store.set('isSubmitting', false);
        if (!response.isSuccess || !response.data) {
            return;
        }
        onSaved(new TaskModel(response.data.item));

        store.update({
            isSubmitted: false,
            isLoading: true,
            newFiles: [],
        });
    };

    if (store.isLoading) {
        return <UiLoading />;
    }

    return (
        <>
            {store.taskTrackerTask.id === 0 && (
                <UiModalTitle className="md-task-tracker-task-save__title">
                    <UiIcon.SquarePlus size={28} />
                    <span>Создать задачу</span>
                </UiModalTitle>
            )}
            {store.taskTrackerTask.id > 0 && (
                <UiModalTitle className="md-task-tracker-task-save__title">
                    <UiIcon.PencilLine size={28} />
                    <span>Изменить задачу</span>
                </UiModalTitle>
            )}
            <UiModalContent>
                <UiForm onSubmit={handleSubmit}>
                    <UiFormControl error={store.isSubmitted && validation.name.error}>
                        <UiInput
                            className="md-task-tracker-task-save__name"
                            placeholder={'Введите название'}
                            template={'large'}
                            onChange={handleChange}
                            name={'name'}
                            value={store.taskTrackerTask.name}
                            isReadOnly={isAssignmentOnly}
                        />
                    </UiFormControl>
                    <UiDescription
                        isOpened
                        className="md-task-tracker-task-save__description"
                        placeholder={'Подробное описание задачи'}
                        onChange={handleChange}
                        name={'description'}
                        value={store.taskTrackerTask.description}
                        isReadOnly={isAssignmentOnly}
                    >
                        {/*<UiMediaUpload/>*/}
                        <CTaskFiles
                            isEditable={!isAssignmentOnly}
                            files={store.taskTrackerTask.files}
                            newFiles={store.newFiles}
                            onChangeFiles={files => store.taskTrackerTask.update({ files })}
                            onChangeNewFiles={files => store.set('newFiles', files)}
                        />
                    </UiDescription>
                    <div className="grid grid-gap-24">
                        <UiFormControl className="grid-col-6--desktop grid-col-12" label="Крайний срок">
                            <UiDatepicker
                                scrollIntoView
                                onChange={handleChange}
                                name={'expiredAt'}
                                value={store.taskTrackerTask.expiredAt}
                                minDate={(store.priorityIsChanged
                                    ? store.taskTrackerTask.minimumExpiredAtFromNowMoment
                                    : store.taskTrackerTask.minimumExpiredAtMoment).format()}
                                closeOnSelect={false}
                                dateTimeMode={store.taskTrackerTask.id === 0 ? 'nextHour' : 'preserve'}
                                hideTimeInputs
                                withTime
                                isReadOnly={isAssignmentOnly}
                            />
                        </UiFormControl>
                        <UiFormControl className="grid-col-6--desktop grid-col-12" label="Приоритет">
                            <UiSelect
                                items={TaskTrackerTaskPriorityEnum.items}
                                onChange={handleChange}
                                name={'priorityId'}
                                value={store.taskTrackerTask.priorityId}
                                isDisabled={isAssignmentOnly}
                                slots={{
                                    controlValue: ({ value, item }) => (
                                        <>
                                            <UiIcon.Dot size={16} strokeWidth={16} color={item?.color} />
                                            <span>{value}</span>
                                        </>
                                    ),
                                    itemValue: ({ item }) => (
                                        <>
                                            <UiIcon.Dot size={16} strokeWidth={16} color={item?.color} />
                                            <span>{item.name}</span>
                                        </>
                                    ),
                                }}
                            />
                        </UiFormControl>
                        <UiFormControl className="grid-col-6--desktop grid-col-12" label="Ответственный">
                            <UiUserSelect
                                value={store.taskTrackerTask.responsibleUserId}
                                onChange={handleChange}
                                name={'responsibleUserId'}
                            />
                        </UiFormControl>
                        <UiFormControl className="grid-col-6--desktop grid-col-12" label="Постановщик">
                            <UiUserSelect
                                value={store.taskTrackerTask.ownerUserId}
                                onChange={handleChange}
                                name={'ownerUserId'}
                                isReadOnly={isAssignmentOnly}
                            />
                        </UiFormControl>
                        <UiFormControl className="grid-col-12" label="Участники">
                            <UiUserSelect
                                multiple
                                value={store.taskTrackerTask.users}
                                onChange={handleChange}
                                name={'users'}
                                preventReSelection={true}
                            />
                        </UiFormControl>
                        {!store.taskTrackerTask.boardId && (
                            <UiFormControl
                                className="grid-col-12"
                                label="Пространство"
                                error={store.isSubmitted && !store.taskTrackerTask.spaceId && 'обязательное поле'}
                            >
                                <UiSelect
                                    scrollIntoView
                                    withSearch
                                    clearSearchOnClose
                                    searchPlaceholder="Поиск по названию"
                                    value={store.taskTrackerTask.spaceId}
                                    items={store.taskTrackerSpaces}
                                    onChange={handleChange}
                                    name={'spaceId'}
                                    isDisabled={isAssignmentOnly}
                                    styles={{
                                        items: {
                                            width: 380,
                                            minWidth: 380,
                                        },
                                    }}
                                />
                            </UiFormControl>
                        )}
                    </div>
                </UiForm>
            </UiModalContent>
            <UiModalActions>
                <UiButton
                    isLoading={store.isSubmitting}
                    template="medium"
                    onClick={handleSubmit}
                >
                    {!store.taskTrackerTask.id ? 'Создать' : 'Сохранить'}
                </UiButton>
                <UiButton
                    isLoading={store.isSubmitting}
                    template="medium"
                    style={{
                        backgroundColor: ['transparent', 'transparent'],
                        color: [COLORS._858991, COLORS._15171C],
                        borderColor: [COLORS._858991, COLORS._15171C],
                    }}
                    onClick={() => {
                        if (!store.taskTrackerTask.id) {
                            onClose();
                            return;
                        }
                        onChangeState('show');
                    }}
                >
                    Отменить
                </UiButton>
            </UiModalActions>
        </>
    );
});
